import { Box, Stack, Typography } from '@mui/material';
import NumberInput from '~/components/Inputs/NumberInput';

interface AsymmetricMarginControlsProps {
    leftMargin: number;
    rightMargin: number;
    anteriorMargin: number;
    posteriorMargin: number;
    superiorMargin: number;
    inferiorMargin: number;
    onLeftMarginChange: (value: number) => void;
    onRightMarginChange: (value: number) => void;
    onAnteriorMarginChange: (value: number) => void;
    onPosteriorMarginChange: (value: number) => void;
    onSuperiorMarginChange: (value: number) => void;
    onInferiorMarginChange: (value: number) => void;
}

const MAX_VALUE = 10;

const inputStyle = {
    textAlign: 'center' as const,
    width: '50px',
    fontSize: 13,
    paddingTop: 5,
    paddingBottom: 4,
};

export default function AsymmetricMarginControls({
    leftMargin,
    rightMargin,
    anteriorMargin,
    posteriorMargin,
    superiorMargin,
    inferiorMargin,
    onLeftMarginChange,
    onRightMarginChange,
    onAnteriorMarginChange,
    onPosteriorMarginChange,
    onSuperiorMarginChange,
    onInferiorMarginChange,
}: AsymmetricMarginControlsProps) {
    return (
        <Stack spacing={0.75}>
            <Typography fontSize={11} color="text.secondary" textAlign="right">
                Values in cm
            </Typography>
            <MarginInputRow
                leftLabel="Right"
                rightLabel="Left"
                leftValue={rightMargin}
                rightValue={leftMargin}
                onLeftChange={onRightMarginChange}
                onRightChange={onLeftMarginChange}
            />
            <MarginInputRow
                leftLabel="Posterior"
                rightLabel="Anterior"
                leftValue={posteriorMargin}
                rightValue={anteriorMargin}
                onLeftChange={onPosteriorMarginChange}
                onRightChange={onAnteriorMarginChange}
            />
            <MarginInputRow
                leftLabel="Superior"
                rightLabel="Inferior"
                leftValue={superiorMargin}
                rightValue={inferiorMargin}
                onLeftChange={onSuperiorMarginChange}
                onRightChange={onInferiorMarginChange}
            />
        </Stack>
    );
}

interface MarginInputRowProps {
    leftLabel: string;
    rightLabel: string;
    leftValue: number;
    rightValue: number;
    onLeftChange: (value: number) => void;
    onRightChange: (value: number) => void;
}

function MarginInputRow({
    leftLabel,
    rightLabel,
    leftValue,
    rightValue,
    onLeftChange,
    onRightChange,
}: MarginInputRowProps) {
    return (
        <Stack direction="row" spacing={2}>
            <MarginInput
                label={leftLabel}
                value={leftValue}
                onChange={onLeftChange}
            />
            <MarginInput
                label={rightLabel}
                value={rightValue}
                onChange={onRightChange}
            />
        </Stack>
    );
}

interface MarginInputProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
}

function MarginInput({ label, value, onChange }: MarginInputProps) {
    return (
        <Box
            sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
            }}
        >
            <Typography
                fontSize={12}
                color="text.secondary"
                sx={{ width: 60, flexShrink: 0 }}
            >
                {label}
            </Typography>
            <NumberInput
                value={value}
                onChange={(v) => onChange(v ?? 0)}
                min={0}
                max={MAX_VALUE}
                step={0.1}
                precision={1}
                inputProps={{ style: inputStyle }}
            />
        </Box>
    );
}
